import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, Button, Loader, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';

import { useGetPatientDocumentsQuery } from '@/store/slices/api/patient/patientDocumentsApiSlice';
import DocumentIcon from '@/assets/icons/DocumentIcon';

import DocumentCard from './DocumentCard';
import styles from './Documents.module.scss';
import DocumentUploadModal from './DocumentUploadModal';

const Documents = () => {
  const [
    documentUploadModalOpened,
    { open: openDocumentUploadModal, close: closeDocumentUploadModal }
  ] = useDisclosure(false);
  const { uuid: patientId } = useParams() as { uuid: string };
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const {
    data: documentsData,
    error: documentsError,
    isLoading: documentsLoading,
    refetch: refetchDocuments
  } = useGetPatientDocumentsQuery(
    { patientId: patientId!, params: { limit, page } },
    { skip: !patientId }
  );

  const handleUploadSuccess = useCallback(() => {
    refetchDocuments();
    closeDocumentUploadModal();
    notifications.show({
      title: 'Success',
      message: 'Document uploaded successfully',
      color: 'green'
    });
  }, [refetchDocuments, closeDocumentUploadModal]);

  if (!patientId) {
    return <div className={styles.noPatient}>No Patient associated with this report.</div>;
  }

  if (documentsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Loader size="md" />
          <Text size="sm" c="dimmed" mt="md">
            Loading documents...
          </Text>
        </div>
      </div>
    );
  }

  if (documentsError) {
    return (
      <div className={styles.container}>
        <Alert color="red" title="Failed to load documents. Please try again." variant="light">
          <Button variant="light" size="xs" mt="sm" onClick={() => refetchDocuments()}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const documents = documentsData?.data || [];
  const hasNoDocuments = documents.length === 0;

  return (
    <div className={styles.container}>
      {!hasNoDocuments && (
        <div className={styles.header}>
          <Button
            size="xs"
            onClick={openDocumentUploadModal}
            variant="subtle"
            leftSection={<IconPlus size={16} />}>
            Add Document
          </Button>
        </div>
      )}

      {hasNoDocuments ? (
        <div className={styles.emptyState}>
          <DocumentIcon />
          <Text size="lg" fw={500} c="dimmed" mt="md">
            No documents uploaded
          </Text>
          <Text size="sm" c="dimmed" mt="xs">
            Upload your first document to get started
          </Text>
          <Button mt="lg" onClick={openDocumentUploadModal}>
            Upload Document
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.documentsList}>
            {documents.map((doc) => (
              <DocumentCard key={doc.id} document={doc} patientId={patientId} />
            ))}
          </div>

          {documentsData && documentsData.totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="subtle"
                size="xs"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Text size="sm" c="dimmed">
                Page {page} of {documentsData.totalPages}
              </Text>
              <Button
                variant="subtle"
                size="xs"
                disabled={page === documentsData.totalPages}
                onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <DocumentUploadModal
        isOpen={documentUploadModalOpened}
        onClose={closeDocumentUploadModal}
        onUploadSuccess={handleUploadSuccess}
        patientId={patientId}
      />
    </div>
  );
};

export default Documents;
